import PlaceholderImage from '@resources/icons/PlaceholderImage.svg';
import { Autocomplete } from '@stereograph/teia-system-design/components/Autocomplete';
import { Box } from '@stereograph/teia-system-design/components/Box';
import { Button } from '@stereograph/teia-system-design/components/Button';
import { Stack } from '@stereograph/teia-system-design/components/Stack';
import { Textarea } from '@stereograph/teia-system-design/components/Textarea';
import { TextField } from '@stereograph/teia-system-design/components/TextField';

import { useGetAllUserByInstanceToken } from '@api/identity/IdentityApiHooks';
import {
  AnomalyPriority,
  AnomalyStatus,
  AnomalyType,
  ObjectAnomalyInformation,
} from '@api/twin/AnomalyApi';
import { usePluginTranslation } from '@translation';
import { Controller, useFormContext } from 'react-hook-form';
import * as z from 'zod';
import { useAnomalyPriorities } from '../../hooks/useAnomalyPriorities';
import { useAnomalyStatus } from '../../hooks/useAnomalyStatus';
import { useAnomalyTypes } from '../../hooks/useAnomalyTypes';
import { AnomalyFormAssignedToHelperText } from './AnomalyFormAssignedToHelperText';

export const anomalySchema = z.object({
  title: z.string().trim()
    .min(1)
    .max(100),
  assignedTo: z.union([z.string().email(), z.literal('')]).default(''),
  stateByGuid: z.record(z.string(), z.nativeEnum(ObjectAnomalyInformation)),
  description: z.string().trim()
    .min(1),
  type: z.nativeEnum(AnomalyType),
  priority: z.nativeEnum(AnomalyPriority),
  status: z.nativeEnum(AnomalyStatus),
  labels: z.array(z.string().trim()
    .min(1)).optional(),
  viewpoint: z.string().optional(),
});

const emailSchema = z.string().email();

export const anomalyFormSchema = z.object({
  anomalySchema,
});

export type AnomalyFormSchema = z.infer<typeof anomalyFormSchema>;

interface AnomalyFormProps {
  buttonLabel: string;
  canSubmit?: boolean;
  snapshot?: string;
}

export const AnomalyForm = (props: AnomalyFormProps) => {
  const { buttonLabel, canSubmit = false, snapshot } = props;
  const { t } = usePluginTranslation();
  const { control } = useFormContext<AnomalyFormSchema>();
  const anomalyPriorities = useAnomalyPriorities();
  const anomalyStatus = useAnomalyStatus();
  const anomalyTypes = useAnomalyTypes();
  const { data: users } = useGetAllUserByInstanceToken();

  return (
    <Stack direction="column" spacing={4}>
      <Stack direction="row" spacing={6}>
        <Box sx={{
          maxWidth: '320px',
          minHeight: '100%',
        }}
        >
          <Box
            component="img"
            // @ts-expect-error alt is not a valid prop for Box because type is not inferred
            alt=""
            src={snapshot ? `data:image/png;base64,${snapshot}` : PlaceholderImage}
            sx={{
              display: 'block',
              height: 'auto',
              minWidth: 80,
              maxWidth: 176,
              minHeight: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
        <Stack
          direction="column"
          spacing={4}
          sx={{
            width: '100%',
          }}
        >
          <Controller
            rules={{
              required: true,
              min: 1,
            }}
            control={control}
            name="anomalySchema.title"
            render={({ field, fieldState: { error }}) => (
              <TextField
                id={field.name}
                label={t('anomaly_plugin.widget.form.title.label')}
                placeholder={t('anomaly_plugin.widget.form.title.placeholder')}
                error={error !== undefined}
                helperText={error?.message}
                required
                onChange={field.onChange}
                name={field.name}
                value={field.value}
                data-dd-action-name="tvp.anomaly.form.title"
              />
            )}
          />
          <Controller
            control={control}
            name="anomalySchema.assignedTo"
            render={({ field, fieldState: { error }}) => (
              <Autocomplete<string, false, false, true>
                freeSolo
                disablePortal
                id={field.name}
                value={field.value}
                options={users?.map((user) => user.email) ?? []}
                onInputChange={(_, value) => {
                  field.onChange(value);
                }}
                selectOnFocus
                renderInput={(params) => (
                  <>
                    <TextField
                      {...params}
                      type="email"
                      error={error !== undefined}
                      helperText={error?.message}
                      label={t('anomaly_plugin.widget.form.assigned_to.label')}
                      data-dd-action-name="tvp.anomaly.form.assigned_to"
                    />
                    <AnomalyFormAssignedToHelperText
                      inputValue={field.value}
                      isValueInUsers={users?.some((user) => user.email === field.value) ?? false}
                      isEmail={emailSchema.safeParse(field.value).success}
                    />
                  </>
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="anomalySchema.description"
            render={({ field, fieldState: { error }}) => (
              <Textarea
                sx={{
                  minWidth: 200,
                }}
                id={field.name}
                placeholder={t('anomaly_plugin.widget.form.description.placeholder')}
                label={t('anomaly_plugin.widget.form.description.label')}
                error={error !== undefined}
                helperText={error?.message}
                required
                onChange={field.onChange}
                name={field.name}
                value={field.value}
              />
            )}
          />

          <Stack direction="row" spacing={4}>
            <Box sx={{
              width: 'calc(100% / 3)',
            }}
            >
              <Controller
                control={control}
                name="anomalySchema.status"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Autocomplete<AnomalyStatus, false, true>
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    isOptionEqualToValue={(option, value) => option === value}
                    getOptionLabel={(option) => anomalyStatus[option]}
                    disableClearable
                    disablePortal
                    id={field.name}
                    options={Object.keys(anomalyStatus) as Array<AnomalyStatus>}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={t('anomaly_plugin.widget.form.status.label')}
                        data-dd-action-name="tvp.anomaly.form.status"
                      />
                    )}
                  />
                )}
              />
            </Box>
            <Box sx={{
              width: 'calc(100% / 3)',
            }}
            >
              <Controller
                control={control}
                name="anomalySchema.priority"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Autocomplete<AnomalyPriority, false, true>
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    isOptionEqualToValue={(option, value) => option === value}
                    getOptionLabel={(option) => anomalyPriorities[option]}
                    disableClearable
                    disablePortal
                    id={field.name}
                    options={Object.keys(anomalyPriorities) as Array<AnomalyPriority>}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={t('anomaly_plugin.widget.form.priority.label')}
                        data-dd-action-name="tvp.anomaly.form.priority"
                      />
                    )}
                  />
                )}
              />
            </Box>
            <Box sx={{
              width: 'calc(100% / 3)',
            }}
            >
              <Controller
                control={control}
                name="anomalySchema.type"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Autocomplete<AnomalyType, false, true>
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    isOptionEqualToValue={(option, value) => option === value}
                    getOptionLabel={(option) => anomalyTypes[option]}
                    disablePortal
                    id={field.name}
                    options={Object.keys(anomalyTypes) as Array<AnomalyType>}
                    disableClearable
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={t('anomaly_plugin.widget.form.type.label')}
                        data-dd-action-name="tvp.anomaly.form.type"
                      />
                    )}
                  />
                )}
              />
            </Box>
          </Stack>
        </Stack>
      </Stack>
      <Button
        type="submit"
        sx={{
          ml: 'auto',
        }}
        disabled={!canSubmit}
      >
        {buttonLabel}
      </Button>
    </Stack>
  );
};
